import { tradeRecords } from '@/mocks/tradingData';

export interface MonthlyPerformanceRow {
  key: string;
  short: string;
  month: string;
  year: string;
  trades: number;
  wins: number;
  losses: number;
  winRate: number;
  netProfit: number;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const monthIndex = (short: string): number => MONTH_SHORT.indexOf(short);

// Groups the single trade ledger into calendar months so the journal can show a
// time dimension — how many trades fell in each month and what that month netted.
// Like every other headline figure on the site, this is derived, never hard-coded.
const deriveMonthlyPerformance = (): MonthlyPerformanceRow[] => {
  const map = new Map<string, MonthlyPerformanceRow>();

  tradeRecords.forEach((trade) => {
    const parts = trade.date.split(' ');
    const short = parts[0];
    const year = parts[2] ?? '';
    const key = `${short} ${year}`;

    const existing =
      map.get(key) ??
      ({
        key,
        short,
        month: MONTH_FULL[monthIndex(short)] ?? short,
        year,
        trades: 0,
        wins: 0,
        losses: 0,
        winRate: 0,
        netProfit: 0,
      } as MonthlyPerformanceRow);

    existing.trades += 1;
    existing.netProfit += trade.profit;
    if (trade.profit > 0) existing.wins += 1;
    else existing.losses += 1;

    map.set(key, existing);
  });

  return Array.from(map.values())
    .sort((a, b) => monthIndex(a.short) - monthIndex(b.short))
    .map((row) => ({
      ...row,
      winRate: row.trades > 0 ? (row.wins / row.trades) * 100 : 0,
    }));
};

export const monthlyPerformance = deriveMonthlyPerformance();

// Largest absolute monthly result — used to scale every bar against a shared axis.
export const monthlyPerformanceMaxAbs = monthlyPerformance.reduce(
  (max, row) => Math.max(max, Math.abs(row.netProfit)),
  0,
);